import React, { useState, useEffect, useCallback } from 'react';
import PageHeader from '../../components/common/PageHeader';
import InventoryCompoundGrid from '../../components/inventory/InventoryCompoundGrid';
import CompoundDetailPanel from '../../components/inventory/CompoundDetailPanel';
import AddToStockForm from '../../components/inventory/AddToStockForm';
import LocationTree from '../../components/inventory/LocationTree';
import ActivityFeed from '../../components/inventory/ActivityFeed';
import InventoryDashboardStats from '../../components/inventory/InventoryDashboardStats';
import { InventoryCompound, InventoryDashboard } from '../../types/inventory';
import { inventoryAPI } from '../../services/inventoryApi';

type InventoryTab = 'compounds' | 'add-to-stock' | 'locations' | 'activity';

const TABS: { id: InventoryTab; label: string }[] = [
  { id: 'compounds', label: 'Compounds' },
  { id: 'add-to-stock', label: 'Add to Stock' },
  { id: 'locations', label: 'Locations' },
  { id: 'activity', label: 'Activity' },
];

const InventoryPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<InventoryTab>('compounds');
  const [selectedCompound, setSelectedCompound] = useState<InventoryCompound | null>(null);
  const [detailPanelOpen, setDetailPanelOpen] = useState(false);
  const [openStockEditor, setOpenStockEditor] = useState(false);
  const [dashboard, setDashboard] = useState<InventoryDashboard | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    inventoryAPI.getDashboard()
      .then(res => setDashboard(res.data))
      .catch(() => {});
  }, [refreshKey]);

  const handleCompoundClick = useCallback((compound: InventoryCompound) => {
    setSelectedCompound(compound);
    setOpenStockEditor(false);
    setDetailPanelOpen(true);
  }, []);

  const handleEditStock = useCallback((compound: InventoryCompound) => {
    setSelectedCompound(compound);
    setOpenStockEditor(true);
    setDetailPanelOpen(true);
  }, []);

  const handleCloseDetail = useCallback(() => {
    setDetailPanelOpen(false);
    setSelectedCompound(null);
    setOpenStockEditor(false);
  }, []);

  const handleRegistered = useCallback(() => {
    setRefreshKey(k => k + 1);
    setActiveTab('compounds');
  }, []);

  return (
    <div className="flex flex-col h-full">
      <PageHeader
        title="Company Inventory"
        subtitle="Physical stock management, batch tracking, and scientific intelligence"
        breadcrumbs={[{ label: 'Inventory' }]}
      />

      {/* Dashboard stats bar */}
      <InventoryDashboardStats dashboard={dashboard} />

      {/* Tab bar */}
      <div className="flex-shrink-0 px-4 flex gap-0.5" style={{ borderBottom: '1px solid var(--card-border)' }}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            style={{
              padding: '8px 16px',
              fontSize: '13px',
              fontWeight: 500,
              color: activeTab === tab.id ? '#0D9488' : 'var(--text-secondary)',
              borderBottom: activeTab === tab.id ? '2px solid #0D9488' : '2px solid transparent',
              background: 'transparent',
              border: 'none',
              borderBottomStyle: 'solid',
              cursor: 'pointer',
              transition: 'color 0.15s',
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      <div className="flex-1 overflow-hidden" style={{ display: 'flex' }}>
        <div style={{ flex: 1, overflow: 'auto', position: 'relative' }}>
          {activeTab === 'compounds' && (
            <InventoryCompoundGrid
              key={refreshKey}
              onCompoundClick={handleCompoundClick}
              onEditStock={handleEditStock}
            />
          )}
          {activeTab === 'add-to-stock' && (
            <AddToStockForm onAdded={handleRegistered} />
          )}
          {activeTab === 'locations' && (
            <LocationTree />
          )}
          {activeTab === 'activity' && (
            <ActivityFeed />
          )}
        </div>

        {/* Slide-out detail panel */}
        {detailPanelOpen && selectedCompound && (
          <CompoundDetailPanel
            compound={selectedCompound}
            onClose={handleCloseDetail}
            autoEditStock={openStockEditor}
          />
        )}
      </div>
    </div>
  );
};

export default InventoryPage;
